import { NodeProps, useReactFlow, Position } from '@xyflow/react'
import { Handle } from '@xyflow/react'
import { Steps } from 'intro.js-react'
import { UserPlus, Settings, Filter, HelpCircle } from 'lucide-react'
import React, { memo } from 'react'

import NodeBaseContainer from '@/components/nodes/base/base-container/NodeBaseContainer'
import NodeConfigurationDialog from '@/components/dialogs/configuration-dialog/NodeConfigurationDialog'
import NodeDataSettingsDialog from '@/components/dialogs/data-settings-dialog/NodeDataSettingsDialog'
import { JavascriptContent } from './components/JavascriptContent'
import { useJavascriptNodeState, useJavascriptExecution, useJavascriptUI } from './hooks'
import type { NodeJavascripterData } from './types'

// Use CSS from QuickStartNode for intro styles
import '~/styles/introjs.css'

/**
 * NodeJavascript - JavaScript node component.
 * 
 * This component has been decomposed following the new architecture patterns:
 * - State management extracted to useJavascriptNodeState hook
 * - Execution logic extracted to useJavascriptExecution hook
 * - UI logic extracted to useJavascriptUI hook
 * - Main content extracted to JavascriptContent component  
 * - Proper AI command pattern implementation using aiCommandUtils
 * - Improved separation of concerns and reusability
 */
const NodeJavascript: React.FC<NodeProps & { data: NodeJavascripterData }> = (props) => {
  const { id, data, selected } = props
  const { updateNodeData } = useReactFlow()
  
  // Use the state management hook
  const {
    // Collaborative state values
    persistedCode,
    persistedRequirements,
    persistedContext,
    linkedKnowledge,
    assignedEmails,

    // Local UI state
    codeInputState,
    isLocked,
    isMinimized,
    minimizeLevel,
    showDebugInfo,
    nodeConfig,
    dataSettings,
    
    // Computed state
    executionState,
    
    // Dialog states
    dialogStates,
    introState,

    // State setters
    setShowDebugInfo,

    // Dialog management
    openDialog,
    closeDialog,

    // Input handlers
    handleCodeChange,
    handleRequirementsChange,
    handleContextChange,
    handleCommandChange,
    handleCodeBlur,
    handleRequirementsBlur,
    handleContextBlur,

    // Action handlers
    handleLock,
    handleMinimize,
    handleMinimizeLevelChange,
    handleConfigChange,
    handleDataSettingsChange,

    // Processing handlers
    handleStartProcessing,
    handleStopProcessing,

    // Intro handlers
    handleStartIntro,
    handleStopIntro,

    // Collaboration utilities
    syncNodeData,
  } = useJavascriptNodeState(id, data, updateNodeData)

  // Use execution logic hook
  const {
    startProcessing,
    executeJavaScript,
    acceptResult,
    resetProcessing,
  } = useJavascriptExecution({
    data,
    codeInputState,
    persistedCode,
    persistedRequirements,
    persistedContext,
    isProcessing: executionState.isProcessing,
    syncNodeData,
    handleStartProcessing,
    handleStopProcessing,
    handleCodeChange,
  })

  // Use UI logic hook
  const {
    customButtons,
    introOptions,
    nodeStatus,
  } = useJavascriptUI({
    openDialog,
    handleStartIntro,
    isProcessing: executionState.isProcessing,
    hasError: executionState.hasError,
    hasResult: executionState.hasResult,
    hasExecutionResult: executionState.hasExecutionResult,
  })

  // Map icon names to actual components for custom buttons
  const iconMap = {
    UserPlus: <UserPlus />,
    Settings: <Settings />,
    Filter: <Filter />,
    HelpCircle: <HelpCircle />,
  }

  // Convert custom buttons with actual icon components
  const buttonsWithIcons = customButtons.map(button => ({
    ...button,
    icon: iconMap[button.icon as keyof typeof iconMap] || button.icon,
  }))
  return (
    <>
      <NodeBaseContainer
        id={id}
        data={data}
        resizable={true}
        nodeTitle={data.label || 'JavaScript'}
        isLocked={isLocked}
        isMinimized={isMinimized}
        minimizeLevel={minimizeLevel}
        onLockChange={handleLock}
        onMinimizeChange={handleMinimize}
        onMinimizeLevelChange={handleMinimizeLevelChange}
        onTitleChange={(title) => syncNodeData({ label: title })}
        customButtons={buttonsWithIcons}
        assignedEmails={assignedEmails ? assignedEmails.split(',').map(email => email.trim()) : []}
        onAssignUsersClick={() => openDialog('introDialog')} // Placeholder
        onConfigClick={() => openDialog('configDialog')}
        onDataSettingsClick={() => openDialog('dataSettingsDialog')}
        onToggleDebugInfo={() => setShowDebugInfo(!showDebugInfo)}
        isProcessing={executionState.isProcessing}
        showDebugInfo={showDebugInfo}
        status={nodeStatus === 'idle' ? 'initial' : nodeStatus === 'processing' ? 'loading' : nodeStatus as any}
        selected={selected}
      >
        {minimizeLevel === 'normal' && !isMinimized && (
          <JavascriptContent
            data={data}
            codeInputState={codeInputState}
            executionState={executionState}
            dataSettings={dataSettings}
            linkedKnowledge={linkedKnowledge}
            showDebugInfo={showDebugInfo}
            isLocked={isLocked}
            onCodeChange={handleCodeChange}
            onRequirementsChange={handleRequirementsChange}
            onContextChange={handleContextChange}
            onCommandChange={handleCommandChange}
            onCodeBlur={handleCodeBlur}
            onRequirementsBlur={handleRequirementsBlur}
            onContextBlur={handleContextBlur}
            onStartProcessing={startProcessing}
            onExecuteJavaScript={executeJavaScript}
            onAcceptResult={acceptResult}
            onResetProcessing={resetProcessing}
            nodeId={id}
          />
        )}

        {/* Node configuration dialog */}
        {dialogStates.configDialog && !isLocked && (
          <NodeConfigurationDialog
            visible={dialogStates.configDialog}
            configuration={nodeConfig}
            onSave={(newConfig) => {
              handleConfigChange(newConfig)
              closeDialog('configDialog')
            }}
            onHide={() => closeDialog('configDialog')}
          />
        )}

        {/* Data settings dialog */}
        {dialogStates.dataSettingsDialog && (
          <NodeDataSettingsDialog
            visible={dialogStates.dataSettingsDialog}
            onHide={() => closeDialog('dataSettingsDialog')}
            settings={dataSettings}
            nodeId={id}
            onSettingsChange={handleDataSettingsChange}
            onRemoveInputLink={() => {}} // TODO: Implement
            onSyncInputLink={async (_nodeId: string) => ({ success: true })} // TODO: Implement
            forceShowAvailableNodes={true}
            debug={showDebugInfo}
          />
        )}

        {/* Steps for intro guide */}        <Steps
          enabled={introState.enabled}
          steps={introState.steps}
          initialStep={introState.stepsIndex}
          onExit={handleStopIntro}
          onBeforeChange={(_nextStepIndex) => {
            // Update the step index - could use setIntroState if needed
            return false
          }}
          options={introOptions}
        />

        {/* Input/Output handles for node connections */}
        <Handle
          type="target"
          position={Position.Top}
          style={{ background: '#555' }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ background: '#555' }}
        />
      </NodeBaseContainer>
    </>
  )
}

export default memo(NodeJavascript)
